import React from "preact/compat";
import {CarBrandsListProps} from "@/elements-react/car-brands-list";
import {LayoutStyleTypes} from "@/util-react/layout.tsx";
import defaultStyles from "@/elements-react/car-brands-list/css/car-brands-list.module.scss";
import styles from "@/elements-react/car-brands-list/css/car-brands-list.black-and-blue.module.scss";
import {useTranslation} from "react-i18next";
import {FixLink} from "@/elements-react/fix-link";
import {Url} from "@/helpers/url.ts";
import {Gtag} from "@/services/gtag.ts";
import {Icon} from "@/elements-react/fix-icon";
import rightSvg from "@/assets/right.svg";
import arrowRight from "@/assets/arrow-right.svg";
import {useServices} from "@/services-context.tsx";
import {useMemo, useState} from "preact/hooks";
import {B2cBrand} from "@/generated/graphql/b2c.ts";
import {BrandCard, BrandType, getList} from "@/elements-react/brands-list-images";
import classNames from "classnames";

export const CarBrandsListBlackAndBlue: React.FC<CarBrandsListProps> = ({ type = LayoutStyleTypes.TYPE_LG }) => {
    const { t } = useTranslation();
    const services = useServices();

    const smMaxItemsCount = 14;
    const lgMaxItemsCount = 42;

    const brands = useMemo<B2cBrand[]>(() => {
        if (!services?.brands?.list) return [];
        return [...services.brands.list].sort((a, b) => a.name.localeCompare(b.name));
    }, [services]);

    const maxItemsCount = type === LayoutStyleTypes.TYPE_SM ? smMaxItemsCount : lgMaxItemsCount;
    const carBrands = brands.filter((item) => item.isCarBrand).slice(0, maxItemsCount);
    const spareBrands = getList(brands.filter((item) => !item.isCarBrand)).slice(0, maxItemsCount);

    if (!brands.length) return null;

    const carBrandsSection = (
        <div className={styles['section']}>
            <div className={styles['section-label']}>
                {t("3qbniuvbxv.car-brands" as any, "Car brands" as any)}
            </div>
            <div className={styles['brands-container']}>
                {carBrands.map((item) => (
                    <FixLink
                        className={classNames(defaultStyles.item, styles.item)}
                        href={Url.to("dynamic-route", { dynamicPath: [item.slug] })}
                        onClick={() => Gtag.pushEvent("car_brand_click", item.name)}
                        key={item.slug}
                    >
                         <span className={styles.itemInner}>
                             <span className={defaultStyles.itemText}>{item.name}</span>
                             <Icon className={styles.itemIcon} src={rightSvg as string} width={24} />
                         </span>
                    </FixLink>
                ))}
            </div>
            <div>
                <FixLink className={styles['link']} href={Url.to("brands-page", {}, "type=car")}>
                    {t("904u3tjvgnh.all-car-brands" as any, "All car brands" as any)}
                    <Icon src={arrowRight as string} width={16} />
                </FixLink>
            </div>
        </div>
    );

    const spareBrandsSection = (
        <div className={styles['section']}>
            <div className={styles['section-label']}>
                {t("iivb4hn8nv.manufactures-brands" as any, "Spare parts manufacturers brands" as any)}
            </div>
            <div className={styles['brands-container']}>
                {spareBrands.map((item) => (
                    <BrandCard className={styles['image-item']} brand={item as BrandType}></BrandCard>
                ))}
            </div>
            <div>
                <FixLink className={styles['link']} href={Url.to("brands-page", {}, "type=spare")}>
                    {t("q2x1a46uul9dgobj.all-spare-brands" as any, "All spare parts manufacturers" as any)}
                    <Icon src={arrowRight as string} width={16} />
                </FixLink>
            </div>
        </div>
    );

    return (
        <div className={classNames(styles.container, styles[type])}>
            <div className={styles['heading']}>
                <h2 className={styles.title}>{t("ioujxhvu339gkg.popular-brands" as any, "Popular brands" as any)}</h2>
                <div className={styles.subtitle}>{t("fdsf38jfn39g.popular-brands-subtitle" as any, "Browse our extensive catalog of genuine components from leading global manufacturers" as any)}</div>
            </div>

            {carBrands.length > 0 && carBrandsSection}
            {spareBrands.length > 0 && spareBrandsSection}
        </div>
    )
}
